import type { TextMessagePart, ToolCallMessagePart } from '@assistant-ui/react';
import type {
  PlatformEvent,
  ArtifactReference,
  RunReasoningDeltaData,
  RunResponseDeltaData,
  RunResponseStopData,
  RunFinalAnswerDeltaData,
  RunFinalAnswerStopData,
  RunCompletedData,
  RunFailedData,
  RunToolCallData,
  RunToolResultData,
  RunSubagentInvokedData,
  RunSubagentReturnedData,
  RunSubagentStartedData,
  RunSubagentCompletedData,
  RunSubagentsAllReturnedData,
  RunSkillActivatedData,
  RunSkillDeactivatedData,
  RunAskUserRequestedData,
  RunAskUserAnsweredData,
  RunAskUserCancelledData,
  ContextUtilization,
  TokenUsage,
} from '../types/events';
import type { UIEvent } from '../types/ui-events';
import {
  getOrCreateIteration,
  applyToolResult,
  buildContentFromIterations,
  buildContentWithSubAgents,
  ensureUniqueToolCallIds,
  findOrCreateSubAgent,
  stepsToToolCalls,
  stepsToIterations,
} from './content-builder';
import type { IterationState, SubAgentGroup, SubAgentState } from './content-builder';
import { handleSubAgentProgressEvent } from './subagent-state';

/**
 * Pure state machine for an agent run reconstructed from a stream of
 * `agent.run.*` PlatformEvents. Used by:
 *
 * - `useAsyncInvokeAdapter` — to render live composer-typed runs through
 *   the assistant-ui adapter (with the adapter wrapper layering on
 *   global stores, the pendingTerminal/drain protocol, etc.).
 * - `useAgentRunEvents` — to render any background run that was started
 *   outside the adapter (e.g. via `dispatchAction`) so the same content
 *   appears in the conversation thread without a reload.
 *
 * The reducer is intentionally side-effect free aside from mutating its
 * own `state` argument. All per-runId UI store updates (`runStateStore`)
 * are the caller's responsibility.
 */
/**
 * Synthetic tool name for the inline ask-user (HITL) question card. Carries the
 * full AskUserRequest in its `input`. Rendered prominently in the message column
 * (NOT in the collapsible chain-of-thought) and excluded from CoT derivation.
 */
export const ASK_USER_TOOL_NAME = '__ask_user';

/** Stable, request-scoped tool-call id so answered/cancelled events can find it. */
export function askUserToolCallId(askUserRequestId: string): string {
  return `${ASK_USER_TOOL_NAME}_${askUserRequestId}`;
}

/**
 * True when the state holds a synthesized `__ask_user` part that is still
 * pending — i.e. the run is PARKED on a question. The runtime parks a run by
 * emitting `ask_user_requested` and then `agent.run.completed` (REST also
 * reports the parked run as `completed`), so terminal consumers must consult
 * this instead of trusting `completed` to mean "conversation over".
 */
export function hasPendingAskUser(state: RunStreamState): boolean {
  for (const [, iter] of state.iterations) {
    for (const [, tc] of iter.toolCalls) {
      if (tc.toolName !== ASK_USER_TOOL_NAME) continue;
      const status = (tc.input as { status?: string } | undefined)?.status;
      if (status === undefined || status === 'pending') return true;
    }
  }
  return false;
}

/**
 * Stable Map key for a sub-agent lifecycle event. The classic orchestrator
 * sends the CHILD run id in `runId`; the Claude runtime sends the PARENT's
 * run id there (its sub-agents are in-process Task tools with no run of
 * their own) — in that case the taskId (tool-use id) is the distinguishing
 * id, otherwise every sub-agent would collapse onto one Map entry.
 */
function subAgentEventKey(data: {
  runId?: string;
  parentRunId?: string;
  taskId?: string;
}): string | undefined {
  if (data.runId && data.runId !== data.parentRunId) return data.runId;
  return data.taskId || data.runId || undefined;
}

/** Finds the synthetic __ask_user tool call across iterations and mutates its input. */
function updateAskUserToolCall(
  state: RunStreamState,
  askUserRequestId: string,
  mutate: (input: Record<string, unknown>) => void,
): void {
  const toolCallId = askUserToolCallId(askUserRequestId);
  for (const [, iter] of state.iterations) {
    const tc = iter.toolCalls.get(toolCallId);
    if (tc) {
      if (!tc.input || typeof tc.input !== 'object') tc.input = {};
      mutate(tc.input as Record<string, unknown>);
      return;
    }
  }
  // Out-of-order: answered/cancelled arrived before requested. Seed a minimal
  // entry in the current iteration so the status isn't lost; requested will
  // backfill the questions later (and preserve this status).
  const iter = getOrCreateIteration(state.iterations, state.currentIteration);
  const input: Record<string, unknown> = { askUserRequestId };
  mutate(input);
  iter.toolCalls.set(toolCallId, {
    toolName: ASK_USER_TOOL_NAME,
    toolCallId,
    input,
  });
}

export interface RunStreamState {
  iterations: Map<number, IterationState>;
  currentIteration: number;
  hasFinalAnswer: boolean;
  receivedFinalAnswerStop: boolean;
  subAgentGroups: SubAgentGroup[];
  /** Cache of artifacts from tool_result events; consumed by buildRunContent. */
  artifactCache: Map<string, ArtifactReference>;
  /** Tracks which ui_event tool calls have already been dispatched. */
  dispatchedIds: Set<string>;
  /**
   * Set when a terminal event is processed. `awaiting_input` is the ask_user
   * terminal: the agent asked a question, the run ended, and we're waiting for
   * the human's answer (which starts a fresh follow-up run). The composer input
   * must re-enable on it just like a normal completion.
   *
   * `incomplete` is the out-of-iterations pause: the agent never produced a
   * final answer, so `response` is a progress summary and the run is resumable
   * from a checkpoint. It is NOT an error — the composer re-enables and the UI
   * offers Continue.
   *
   * Precedence (ordering-proof in both arrival orders):
   * `failed`/`cancelled` > `awaiting_input` > `incomplete` > `completed`. The
   * runtime parks an ask_user run by emitting `ask_user_requested` AND a
   * trailing `agent.run.completed` — that completed augments the
   * awaiting_input terminal with durationMs/usage/contextUtilization but never
   * replaces it while the question is still pending. Conversely an
   * `ask_user_requested` arriving after a (possibly poll-synthesized)
   * `completed` flips it back to `awaiting_input`.
   */
  terminal?: {
    kind: 'completed' | 'failed' | 'cancelled' | 'awaiting_input' | 'incomplete';
    runId?: string;
    durationMs?: number;
    response?: string;
    error?: string;
    /** Machine-readable failure code (e.g. MAX_ITERATIONS) from agent.run.failed */
    errorCode?: string;
    /** Why an `incomplete` run stopped: 'MAX_ITERATIONS' | 'NO_PROGRESS' */
    stopReason?: string;
    /** An `incomplete` run whose next turn continues from a checkpoint */
    resumable?: boolean;
    usage?: TokenUsage;
    contextUtilization?: ContextUtilization;
    /** Present when this run responded to a delegation completion event. */
    delegationRef?: import('../types/index').DelegationRef;
  };
}

export interface ApplyRunEventOptions {
  /** Optional UI event dispatcher for ui_event tool calls (called inside buildRunContent). */
  dispatchUIEvent?: (event: UIEvent) => void;
  /** Optional resolver from tool DB id → human-readable name. */
  getToolName?: (nameOrId: string) => string;
}

/** Creates a fresh, empty run-stream state. */
export function createRunStreamState(): RunStreamState {
  return {
    iterations: new Map<number, IterationState>(),
    currentIteration: 0,
    hasFinalAnswer: false,
    receivedFinalAnswerStop: false,
    subAgentGroups: [],
    artifactCache: new Map<string, ArtifactReference>(),
    dispatchedIds: new Set<string>(),
  };
}

/**
 * Applies a single PlatformEvent to RunStreamState, mutating it in place.
 *
 * After processing a terminal event the caller should read `state.terminal`
 * to detect run completion/failure/cancellation.
 */
export function applyRunEvent(
  state: RunStreamState,
  event: PlatformEvent,
  _opts?: ApplyRunEventOptions,
): void {
  const eventData = event.data as Record<string, unknown>;

  // Sub-agent PROGRESS events (`reasoning_*`, `response_*`, `tool_call`,
  // `tool_result`, `final_answer_*` emitted ON the child run, with
  // parentRunId in data) are routed to the matching SubAgentState. Such
  // events must never fall through to the main switch — they belong to a
  // sub-agent and processing them as coordinator events would corrupt the
  // coordinator's iteration state.
  //
  // Sub-agent LIFECYCLE events (`subagent_started`, `subagent_completed`)
  // are emitted by the PARENT about a child and also carry data.parentRunId,
  // but they belong to the main switch below where the status flip lives.
  // Same for `subagent_invoked` / `subagent_returned`. Exempt all
  // `agent.run.subagent_*` event types from the progress route.
  const isSubAgentLifecycle = event.eventType.startsWith('agent.run.subagent_');
  if (eventData?.parentRunId && !isSubAgentLifecycle) {
    handleSubAgentProgressEvent(event, state.subAgentGroups);
    return;
  }

  switch (event.eventType) {
    case 'agent.run.started':
      break;

    // ─── Sub-agent lifecycle events ─────────────────────────────────────

    case 'agent.run.subagent_invoked': {
      const data = event.data as unknown as RunSubagentInvokedData;
      // The Claude runtime emits subagent_started BEFORE subagent_invoked
      // (both derive from the same assistant message), so the started event
      // has already lazily created a group holding these tasks. Opening a
      // second group here would leave it empty and make the later
      // subagent_returned seed duplicate entries into it.
      const taskIds = (data.tasks ?? [])
        .map((t) => t.taskId)
        .filter((id): id is string => !!id);
      const allKnown =
        taskIds.length > 0 &&
        taskIds.every((id) => state.subAgentGroups.some((g) => g.subAgents.has(id)));
      if (allKnown) break;
      const group: SubAgentGroup = {
        spawnedAt: Date.now(),
        spawnIteration: state.currentIteration,
        mode: data.mode,
        pendingTasks: data.tasks,
        subAgents: new Map(),
        allComplete: false,
      };
      state.subAgentGroups.push(group);
      break;
    }

    case 'agent.run.subagent_returned': {
      const data = event.data as unknown as RunSubagentReturnedData;
      // Find the last group with empty subAgents (Phase 1 → Phase 2 transition)
      const targetGroup = state.subAgentGroups.slice().reverse().find(
        (g) => g.subAgents.size === 0,
      );
      for (let i = 0; i < data.tasks.length; i++) {
        const task = data.tasks[i];
        // The Claude runtime's tasks have no childRunId (its sub-agents are
        // in-process Task tools) — key by taskId instead. A missing id must
        // never become the Map key: two groups each holding an `undefined`
        // key would both emit `__subagent_run_undefined` tool-call parts,
        // and duplicate toolCallIds crash assistant-ui's tapResources.
        const key = task.childRunId || task.taskId || `__missing_${i}`;
        if (!task.childRunId && !task.taskId) {
          console.warn('[subagent] subagent_returned task missing childRunId/taskId', { task });
        }
        // If subagent_started already created this entry (Claude runtime
        // ordering), backfill identity fields instead of seeding a duplicate
        // entry into another group.
        const existing = state.subAgentGroups
          .map((g) => g.subAgents.get(key))
          .find((sa): sa is SubAgentState => !!sa);
        if (existing) {
          if (task.agentId) existing.agentId = task.agentId;
          if (task.agentName) existing.agentName = task.agentName;
          continue;
        }
        if (!targetGroup) continue;
        targetGroup.subAgents.set(key, {
          taskId: task.taskId ?? '',
          runId: task.childRunId ?? '',
          agentId: task.agentId ?? task.subagentType ?? '',
          agentName: task.agentName ?? task.subagentType ?? 'Sub-Agent',
          status: 'pending',
          reasoningText: '',
          responseText: '',
          toolCalls: new Map(),
          toolCallCount: 0,
          iterations: new Map(),
          currentIteration: 0,
        });
      }
      if (targetGroup) {
        targetGroup.pendingTasks = undefined;
        if (!data.success && data.error) {
          targetGroup.allComplete = true;
          targetGroup.summary = {
            successCount: 0,
            failureCount: data.count,
            totalDurationMs: data.durationMs,
          };
        }
      }
      break;
    }

    case 'agent.run.subagent_started': {
      const data = event.data as unknown as RunSubagentStartedData;
      // A missing runId must never become a sub-agent key (see
      // subagent_returned above). Mirrors the guard in subagent-state.ts.
      const startedKey = subAgentEventKey(data);
      if (!startedKey) {
        console.warn('[subagent] subagent_started event missing runId/taskId', { data });
        break;
      }
      // Lazily create the entry: the live runtime streams subagent_started
      // (and child progress events) without a preceding subagent_returned.
      const sa = findOrCreateSubAgent(state.subAgentGroups, startedKey, {
        agentId: data.agentId ?? data.subagentType,
        agentName: data.agentName ?? data.subagentType,
        taskId: data.taskId,
      });
      sa.status = 'running';
      sa.startedAt = Date.now();
      if (data.agentName) sa.agentName = data.agentName;
      // Stamp the spawn iteration on the owning group the first time we see it,
      // so the CoT can place the block chronologically. The live runtime
      // creates the group lazily here (no preceding subagent_invoked).
      const startedGroup = state.subAgentGroups.find((g) => g.subAgents.has(startedKey));
      if (startedGroup && startedGroup.spawnIteration === undefined) {
        startedGroup.spawnIteration = state.currentIteration;
      }
      break;
    }

    case 'agent.run.subagent_completed': {
      const data = event.data as unknown as RunSubagentCompletedData;
      // Same missing-id guard as subagent_started.
      const completedKey = subAgentEventKey(data);
      if (!completedKey) {
        console.warn('[subagent] subagent_completed event missing runId/taskId', { data });
        break;
      }
      // find-or-create so a completion that lands without a prior `started`
      // (or `returned`) still records the result.
      const sa = findOrCreateSubAgent(state.subAgentGroups, completedKey, {
        agentId: data.agentId ?? data.subagentType,
        agentName: data.agentName ?? data.subagentType,
        taskId: data.taskId,
      });
      sa.status = data.success ? 'completed' : 'failed';
      sa.durationMs = data.durationMs;
      sa.toolCallCount = data.toolCallCount;
      if (data.agentName) sa.agentName = data.agentName;
      if (data.usage) sa.usage = data.usage;
      if (data.response) sa.responseText = data.response;
      if (data.error) sa.error = data.error;
      break;
    }

    case 'agent.run.subagents_all_returned': {
      const data = event.data as unknown as RunSubagentsAllReturnedData;
      const lastGroup = state.subAgentGroups[state.subAgentGroups.length - 1];
      if (lastGroup) {
        lastGroup.allComplete = true;
        lastGroup.summary = {
          successCount: data.successCount,
          failureCount: data.failureCount,
          totalDurationMs: data.totalDurationMs,
          tasks: data.tasks,
        };
      }
      break;
    }

    // ─── Reasoning events ───────────────────────────────────────────────

    case 'agent.run.reasoning_start': {
      const data = event.data as unknown as { iteration: number };
      state.currentIteration = data.iteration;
      getOrCreateIteration(state.iterations, state.currentIteration);
      break;
    }

    case 'agent.run.reasoning_delta': {
      const data = event.data as unknown as RunReasoningDeltaData;
      const iter = getOrCreateIteration(state.iterations, data.iteration);
      if (data.accumulated != null) {
        if (data.accumulated.length >= iter.reasoningText.length) {
          iter.reasoningText = data.accumulated;
        }
      } else if (data.delta) {
        iter.reasoningText += data.delta;
      }
      break;
    }

    case 'agent.run.reasoning_stop':
      break;

    // ─── Response events ────────────────────────────────────────────────

    case 'agent.run.response_start': {
      const data = event.data as unknown as { iteration: number };
      state.currentIteration = data.iteration;
      getOrCreateIteration(state.iterations, state.currentIteration);
      break;
    }

    case 'agent.run.response_delta': {
      const data = event.data as unknown as RunResponseDeltaData;
      const iter = getOrCreateIteration(state.iterations, data.iteration);
      if (data.accumulated != null) {
        if (data.accumulated.length >= iter.responseText.length) {
          iter.responseText = data.accumulated;
        }
      } else if (data.delta) {
        iter.responseText += data.delta;
      }
      break;
    }

    case 'agent.run.response_stop': {
      const data = event.data as unknown as RunResponseStopData;
      const iter = getOrCreateIteration(
        state.iterations,
        data.iteration ?? state.currentIteration,
      );
      // Apply authoritative full narration from the stop event.
      // Note: do NOT touch iter.isThinking here. `isFinal` only signals
      // that the response slot is closed for this iteration; it does not
      // mean this text is the user-facing final answer. The actual final
      // answer arrives via agent.run.final_answer_* (which promotes prior
      // iterations to thinking) or — for legacy runs without final_answer
      // events — via agent.run.completed (which finalises the last
      // iteration). Flipping isThinking here caused a visible flicker
      // where wrap-up narration briefly rendered as a top-level message
      // before final_answer_start moved it back into the CoT block.
      if (data.content != null && data.content.length >= iter.responseText.length) {
        iter.responseText = data.content;
      }
      break;
    }

    // ─── Final answer events ────────────────────────────────────────────

    case 'agent.run.final_answer_start': {
      const data = event.data as unknown as { iteration: number };
      state.hasFinalAnswer = true;
      state.currentIteration = data.iteration;
      const iter = getOrCreateIteration(state.iterations, state.currentIteration);
      iter.hasFinalAnswer = true;
      for (const [, prevIter] of state.iterations) {
        if (prevIter.isThinking === null || prevIter.isThinking === false) {
          prevIter.isThinking = true;
        }
      }
      break;
    }

    case 'agent.run.final_answer_delta': {
      const data = event.data as unknown as RunFinalAnswerDeltaData;
      const iter = getOrCreateIteration(state.iterations, data.iteration);
      if (data.accumulated != null) {
        if (data.accumulated.length >= iter.finalAnswerText.length) {
          iter.finalAnswerText = data.accumulated;
        }
      } else if (data.delta) {
        iter.finalAnswerText += data.delta;
      }
      break;
    }

    case 'agent.run.final_answer_stop': {
      const data = event.data as unknown as RunFinalAnswerStopData;
      state.receivedFinalAnswerStop = true;
      // Backend's final_answer_stop.content is authoritative — overwrite
      // anything we accumulated from deltas. Self-repairs missing/reordered
      // delta events. `!= null` so an empty string still overwrites
      // (legitimate "no content"); only an absent field preserves accumulated text.
      if (data.content != null) {
        const iter = getOrCreateIteration(
          state.iterations,
          data.iteration ?? state.currentIteration,
        );
        iter.finalAnswerText = data.content;
      }
      break;
    }

    // ─── Tool events ────────────────────────────────────────────────────

    case 'agent.run.tool_call': {
      const data = event.data as unknown as RunToolCallData;
      const iterNum = data.iteration ?? state.currentIteration;
      const iter = getOrCreateIteration(state.iterations, iterNum);
      iter.isThinking = true;
      iter.toolCalls.set(data.toolCallId, {
        toolName: data.toolName,
        toolDisplayName: data.toolDisplayName,
        toolCallId: data.toolCallId,
        input: data.input,
        label: data.label,
      });
      break;
    }

    case 'agent.run.tool_result': {
      const data = event.data as unknown as RunToolResultData;
      if (data.artifacts) {
        for (const ref of data.artifacts) {
          state.artifactCache.set(ref.artifactId, ref);
        }
      }
      applyToolResult(state.iterations, data);
      break;
    }

    // ─── Skill events ───────────────────────────────────────────────────
    // The backend emits dedicated skill_activated/skill_deactivated events
    // instead of tool_call/tool_result for skill operations. Synthesize a
    // completed tool-call entry so the CoT panel renders them like any
    // other tool call.

    case 'agent.run.skill_activated':
    case 'agent.run.skill_deactivated': {
      const data = event.data as unknown as
        | RunSkillActivatedData
        | RunSkillDeactivatedData;
      const toolName =
        event.eventType === 'agent.run.skill_activated'
          ? 'activate_skill'
          : 'deactivate_skill';
      const iterNum = data.iteration ?? state.currentIteration;
      const iter = getOrCreateIteration(state.iterations, iterNum);
      const toolCallId = `skill-${data.skillId}-${iterNum}-${data.timestamp}`;
      iter.toolCalls.set(toolCallId, {
        toolName,
        toolCallId,
        input: { skillId: data.skillId, skillName: data.skillName },
        output: data.success ? 'ok' : undefined,
        error: data.success ? undefined : data.error,
        durationMs: data.durationMs,
      });
      break;
    }

    // ─── Ask-user (HITL) events ─────────────────────────────────────────
    // The backend emits dedicated ask_user_requested/answered/cancelled events
    // (not tool_call/tool_result). Synthesize a `__ask_user` tool-call entry
    // carrying the full request so the message renderer can mount an inline,
    // interactive question card. The card is rendered OUTSIDE the collapsible
    // chain-of-thought (see agent-assistant-message.tsx) and `__ask_user` is
    // excluded from the CoT timeline derivation.

    case 'agent.run.ask_user_requested': {
      const data = event.data as unknown as RunAskUserRequestedData;
      const iterNum = data.iteration ?? state.currentIteration;
      const iter = getOrCreateIteration(state.iterations, iterNum);
      const toolCallId = askUserToolCallId(data.askUserRequestId);
      // Don't clobber a status set by an out-of-order answered/cancelled event.
      const existing = iter.toolCalls.get(toolCallId);
      iter.toolCalls.set(toolCallId, {
        toolName: ASK_USER_TOOL_NAME,
        toolCallId,
        input: {
          askUserRequestId: data.askUserRequestId,
          inboxItemId: data.inboxItemId,
          spaceId: event.spaceId,
          conversationId: data.conversationId,
          questions: data.questions,
          status: (existing?.input as { status?: string } | undefined)?.status ?? 'pending',
          answers: (existing?.input as { answers?: unknown } | undefined)?.answers,
        },
      });
      // ask_user parks the run: it's a terminal boundary (status
      // `awaiting_input`), not a mid-run pause. The runtime ALSO emits a
      // trailing `agent.run.completed` for the park (and REST reports the run
      // as completed), so `awaiting_input` must win over `completed` in both
      // arrival orders — but never over failed/cancelled, and only while the
      // question is actually still pending (an out-of-order answered/cancelled
      // means the run is not parked).
      const nowPending =
        ((iter.toolCalls.get(toolCallId)!.input as { status?: string }).status ??
          'pending') === 'pending';
      if (nowPending && (!state.terminal || state.terminal.kind === 'completed')) {
        state.terminal = {
          ...state.terminal, // keep durationMs/usage/contextUtilization from a prior completed
          kind: 'awaiting_input',
          runId: state.terminal?.runId ?? event.subjectId,
        };
      }
      break;
    }

    case 'agent.run.ask_user_answered':
    case 'agent.run.ask_user_cancelled': {
      const data = event.data as unknown as
        | RunAskUserAnsweredData
        | RunAskUserCancelledData;
      const status =
        event.eventType === 'agent.run.ask_user_answered'
          ? 'answered'
          : 'cancelled';
      const answers = (data as RunAskUserAnsweredData).answers;
      updateAskUserToolCall(state, data.askUserRequestId, (input) => {
        input.status = status;
        if (answers) input.answers = answers;
      });
      break;
    }

    // ─── Terminal events ────────────────────────────────────────────────

    case 'agent.run.completed': {
      const completedData = event.data as unknown as RunCompletedData;
      // A parked run (pending ask_user) also emits `completed` — treat it as
      // augmentation of the awaiting_input terminal, never as "conversation
      // over". Its `response` is the QUESTION text, so seeding it as answer
      // text would render the question as the reply. failed/cancelled still
      // outrank the park.
      const hardTerminal =
        state.terminal?.kind === 'failed' || state.terminal?.kind === 'cancelled';
      // The pending ask part is the single source of truth: an answered ask
      // leaves a stale awaiting_input terminal behind, and that run is NOT
      // parked anymore.
      const parked = !hardTerminal && hasPendingAskUser(state);

      if (state.iterations.size > 0 || state.subAgentGroups.length > 0) {
        const lastIter = getOrCreateIteration(state.iterations, state.currentIteration);
        if (lastIter.isThinking === null) {
          lastIter.isThinking = false;
        }

        if (parked) {
          // Skip response seeding — steps merge below still enriches the trace.
        } else if (state.hasFinalAnswer) {
          if (completedData.response) {
            lastIter.finalAnswerText = completedData.response;
          }
        } else if (completedData.response) {
          lastIter.responseText = completedData.response;
          lastIter.isThinking = false;
        }

        // Merge tool calls from completed steps
        const mergeToolCalls = stepsToToolCalls(completedData.steps);
        if (mergeToolCalls.length) {
          const existingIds = new Set<string>();
          for (const [, iter] of state.iterations) {
            for (const [id] of iter.toolCalls) {
              existingIds.add(id);
            }
          }
          for (const tc of mergeToolCalls) {
            if (!existingIds.has(tc.toolCallId)) {
              lastIter.toolCalls.set(tc.toolCallId, tc);
            } else {
              for (const [, iter] of state.iterations) {
                const existing = iter.toolCalls.get(tc.toolCallId);
                if (existing) {
                  if (
                    tc.input &&
                    (!existing.input ||
                      typeof existing.input === 'string' ||
                      Object.keys(existing.input).length === 0)
                  ) {
                    existing.input = tc.input;
                    state.dispatchedIds.delete(tc.toolCallId);
                  }
                  if (tc.output !== undefined && existing.output === undefined) {
                    existing.output = tc.output;
                  }
                  if (tc.error !== undefined && existing.error === undefined) {
                    existing.error = tc.error;
                  }
                  if (tc.durationMs !== undefined) {
                    existing.durationMs = tc.durationMs;
                  }
                  break;
                }
              }
            }
          }
        }
      } else if (completedData.steps?.length) {
        // No streaming iterations — seed from steps so the post-loop yield
        // has content to work with.
        const stepIterations = stepsToIterations(completedData.steps);
        for (const [key, val] of stepIterations) {
          state.iterations.set(key, val);
        }
        state.hasFinalAnswer = [...stepIterations.values()].some(
          (it) => it.hasFinalAnswer,
        );
      } else if (completedData.response && !parked) {
        // No streaming iterations and no steps — create a simple text iteration
        const iter = getOrCreateIteration(state.iterations, 0);
        iter.responseText = completedData.response;
        iter.isThinking = false;
      }

      if (parked) {
        // Keep the park; absorb the completed's metadata so the CoT header /
        // context bar still get duration and usage.
        state.terminal = {
          kind: 'awaiting_input',
          runId: state.terminal?.runId ?? event.subjectId,
          durationMs: completedData.durationMs ?? state.terminal?.durationMs,
          usage: completedData.usage ?? state.terminal?.usage,
          contextUtilization:
            completedData.contextUtilization ?? state.terminal?.contextUtilization,
        };
        break;
      }

      // A pause must never downgrade a hard terminal: `incomplete` sits below
      // failed/cancelled in the precedence order.
      if (completedData.incomplete && hardTerminal) break;

      state.terminal = {
        // The runtime reuses `agent.run.completed` for the out-of-iterations
        // pause so pre-feature clients still terminate the run. `response` is
        // a progress summary in that case, not an answer.
        kind: completedData.incomplete ? 'incomplete' : 'completed',
        runId: event.subjectId,
        durationMs: completedData.durationMs,
        response: completedData.response,
        usage: completedData.usage,
        contextUtilization: completedData.contextUtilization,
        delegationRef: completedData.delegationRef,
        ...(completedData.incomplete && {
          stopReason: completedData.stopReason,
          resumable: completedData.resumable,
        }),
      };
      break;
    }

    case 'agent.run.failed': {
      const failedData = event.data as unknown as RunFailedData;
      state.terminal = {
        kind: 'failed',
        runId: event.subjectId,
        durationMs: failedData.durationMs,
        error: failedData.error,
        errorCode: failedData.code ?? failedData.errorCode,
      };
      break;
    }

    case 'agent.run.cancelled': {
      state.terminal = {
        kind: 'cancelled',
        runId: event.subjectId,
      };
      break;
    }

    // Canvas / surface events are handled by their own context — no
    // adapter-level processing needed.
    case 'agent.run.canvas_created':
    case 'agent.run.surface_update':
    case 'agent.run.surface_deleted':
      break;

    // Context compaction events are pure UI side effects routed to
    // runStateStore.contextUtilization by the adapter wrapper.
    // Nothing to track in the reducer.
    case 'agent.run.context_compaction_start':
    case 'agent.run.context_compaction_stop':
      break;
  }
}

/**
 * Builds the assistant content array from the current state. Equivalent to
 * the `yieldContent` closure inside the original adapter's processEvents.
 */
export function buildRunContent(
  state: RunStreamState,
  opts?: ApplyRunEventOptions,
): (TextMessagePart | ToolCallMessagePart)[] {
  const hasSubAgents = state.subAgentGroups.length > 0;
  const content = hasSubAgents
    ? buildContentWithSubAgents(
        state.iterations,
        state.subAgentGroups,
        state.hasFinalAnswer,
        opts?.dispatchUIEvent,
        state.dispatchedIds,
        state.artifactCache,
        opts?.getToolName,
      )
    : buildContentFromIterations(
        state.iterations,
        state.hasFinalAnswer,
        opts?.dispatchUIEvent,
        state.dispatchedIds,
        state.artifactCache,
        opts?.getToolName,
      );
  // Duplicate toolCallIds crash assistant-ui (tapResources duplicate-key
  // throw). The builders shouldn't produce any, but backend ids are not
  // under our control — enforce uniqueness on everything we hand over.
  return ensureUniqueToolCallIds(content ?? []) as (TextMessagePart | ToolCallMessagePart)[];
}
